import { ORIGINAL_ASSET_LAYOUT } from "../data/generated/original-asset-layout";

export type OriginalDirection = "up" | "down" | "left" | "right";

export type Rect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PlayerSpriteLayout = {
  source: Rect;
  mask: Rect;
  destination: Rect;
  evidence: {
    sourceStatementId: string;
    maskStatementId: string;
  };
};

export type GaugeSegment = {
  source: Rect;
  destination: Rect;
  evidenceStatementId: string;
};

const PLAYER_SOURCE_RULE = ORIGINAL_ASSET_LAYOUT.playerSprite.originalSourceRectangles[0];
const PLAYER_MASK_RULE = ORIGINAL_ASSET_LAYOUT.playerSprite.originalMaskRectangles[0];
const PLAYER_DESTINATION_RULE = ORIGINAL_ASSET_LAYOUT.playerSprite.originalDestinationRules[0];
const HP_FILLED_RULE = ORIGINAL_ASSET_LAYOUT.uiBars.originalSourceRectangles.find(
  (record) => record.procedure === "HPDraw" && record.sourceRect.yExpression === "0"
);
const HP_EMPTY_RULE = ORIGINAL_ASSET_LAYOUT.uiBars.originalSourceRectangles.find(
  (record) => record.procedure === "HPDraw" && record.sourceRect.yExpression === "12"
);
const MP_FILLED_RULE = ORIGINAL_ASSET_LAYOUT.uiBars.originalSourceRectangles.find(
  (record) => record.procedure === "MPDraw" && record.sourceRect.yExpression === "24"
);
const MP_EMPTY_RULE = ORIGINAL_ASSET_LAYOUT.uiBars.originalSourceRectangles.find(
  (record) => record.procedure === "MPDraw" && record.sourceRect.yExpression === "36"
);

export const ASSET_LAYOUT_RUNTIME_CONSUMPTION = {
  manifest: "docs/original-asset-layout.json",
  generatedManifestModule: "web/src/data/generated/original-asset-layout.ts",
  consumesDecodedLayout: true,
  sourceBackedStatus: ORIGINAL_ASSET_LAYOUT.status,
  sourceBackedCriteria: {
    sourceRectanglesDecoded: ORIGINAL_ASSET_LAYOUT.criteria.sourceRectanglesDecoded,
    destinationRulesDecoded: ORIGINAL_ASSET_LAYOUT.criteria.destinationRulesDecoded,
    maskCoordinatesDecoded: ORIGINAL_ASSET_LAYOUT.criteria.maskCoordinatesDecoded,
    alphaOrMaskCompositionDecoded: ORIGINAL_ASSET_LAYOUT.criteria.alphaOrMaskCompositionDecoded
  },
  consumedStatementIds: {
    playerSource: PLAYER_SOURCE_RULE.statementId,
    playerMask: PLAYER_MASK_RULE.statementId,
    playerDestination: PLAYER_DESTINATION_RULE.statementIds,
    hpFilled: HP_FILLED_RULE?.statementId,
    hpEmpty: HP_EMPTY_RULE?.statementId,
    mpFilled: MP_FILLED_RULE?.statementId,
    mpEmpty: MP_EMPTY_RULE?.statementId
  }
} as const;

export function assetLayoutRuntimeConsumesDecodedLayout(): boolean {
  return ASSET_LAYOUT_RUNTIME_CONSUMPTION.consumesDecodedLayout;
}

export function originalMapTileSourceRect(tileIndex: number): Rect {
  return {
    x: (tileIndex % 16) * 32,
    y: Math.floor(tileIndex / 16) * 32,
    width: 32,
    height: 32
  };
}

export function originalWayForDirection(direction: OriginalDirection): number {
  if (direction === "up") return 0;
  if (direction === "right") return 1;
  if (direction === "down") return 2;
  return 3;
}

export function originalPlayerSpriteLayout(
  direction: OriginalDirection,
  frameIndex: number,
  charType: number
): PlayerSpriteLayout {
  const way = originalWayForDirection(direction);
  const wayA = frameIndex % 2;
  const x = (way * 2 + wayA) * 32;
  const sourceY = charType * 64;
  const maskY = sourceY + 32;
  return {
    source: { x, y: sourceY, width: 32, height: 32 },
    mask: { x, y: maskY, width: 32, height: 32 },
    destination: { x: 160, y: 128, width: 32, height: 32 },
    evidence: {
      sourceStatementId: PLAYER_SOURCE_RULE.statementId,
      maskStatementId: PLAYER_MASK_RULE.statementId
    }
  };
}

export function originalHpGaugeSegments(actorIndex: number, hp: number, maxHp: number): GaugeSegment[] {
  return originalGaugeSegments({
    actorIndex,
    value: hp,
    maxValue: maxHp,
    filledY: 0,
    emptyY: 12,
    destinationY: 26 + actorIndex * 64,
    filledStatementId: HP_FILLED_RULE?.statementId ?? "",
    emptyStatementId: HP_EMPTY_RULE?.statementId ?? ""
  });
}

export function originalMpGaugeSegments(actorIndex: number, mp: number, maxMp: number): GaugeSegment[] {
  return originalGaugeSegments({
    actorIndex,
    value: mp,
    maxValue: maxMp,
    filledY: 24,
    emptyY: 36,
    destinationY: 42 + actorIndex * 64,
    filledStatementId: MP_FILLED_RULE?.statementId ?? "",
    emptyStatementId: MP_EMPTY_RULE?.statementId ?? ""
  });
}

function originalGaugeSegments(options: {
  actorIndex: number;
  value: number;
  maxValue: number;
  filledY: number;
  emptyY: number;
  destinationY: number;
  filledStatementId: string;
  emptyStatementId: string;
}): GaugeSegment[] {
  if (options.maxValue <= 0) return [];
  const value = Math.max(0, Math.min(options.value, options.maxValue));
  const filledWidth = Math.max(0, Math.min(96, originalVbIntegerPixel((96 * value) / options.maxValue)));
  const segments: GaugeSegment[] = [];
  if (value > 0 && filledWidth > 0) {
    segments.push({
      source: { x: 0, y: options.filledY, width: filledWidth, height: 12 },
      destination: { x: 408, y: options.destinationY, width: filledWidth, height: 12 },
      evidenceStatementId: options.filledStatementId
    });
  }
  if (value < options.maxValue && filledWidth < 96) {
    segments.push({
      source: { x: filledWidth, y: options.emptyY, width: 96 - filledWidth, height: 12 },
      destination: { x: 408 + filledWidth, y: options.destinationY, width: 96 - filledWidth, height: 12 },
      evidenceStatementId: options.emptyStatementId
    });
  }
  return segments;
}

function originalVbIntegerPixel(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  const absolute = Math.abs(value);
  const floor = Math.floor(absolute);
  const fraction = absolute - floor;
  if (fraction < 0.5) return sign * floor;
  if (fraction > 0.5) return sign * (floor + 1);
  return sign * (floor % 2 === 0 ? floor : floor + 1);
}
